import * as LDClient from "launchdarkly-js-client-sdk";

/**
 * LaunchDarkly Service
 * Provides a singleton wrapper around LaunchDarkly client for feature flag management
 */
class LaunchDarklyService {
	private static instance: LaunchDarklyService;
	private client: LDClient.LDClient | null = null;
	private isInitialized = false;
	private initializationPromise: Promise<void> | null = null;

	private constructor() { }

	/**
	 * Get the singleton instance
	 */
	public static getInstance(): LaunchDarklyService {
		if (!LaunchDarklyService.instance) {
			LaunchDarklyService.instance = new LaunchDarklyService();
		}
		return LaunchDarklyService.instance;
	}

	/**
	 * Initialize LaunchDarkly client
	 * @param clientSideId - Your LaunchDarkly client-side ID
	 * @param user - User context for targeting (optional)
	 * @returns Promise that resolves when client is ready
	 */
	public async initialize(clientSideId: string, user?: LDClient.LDUser): Promise<void> {
		// If already initialized, return immediately
		if (this.isInitialized && this.client) {
			return Promise.resolve();
		}

		// If initialization is in progress, return existing promise
		if (this.initializationPromise) {
			return this.initializationPromise;
		}

		// Start new initialization
		this.initializationPromise = this._initialize(clientSideId, user);
		return this.initializationPromise;
	}

	private async _initialize(clientSideId: string, user?: LDClient.LDUser): Promise<void> {
		try {
			// Default anonymous user if none provided
			const defaultUser: LDClient.LDUser = user || {
				key: "anonymous-user",
				anonymous: true,
			};

			// Initialize the client
			this.client = LDClient.initialize(clientSideId, defaultUser, {
				// Optional configuration
				streaming: true, // Enable streaming for real-time updates
				bootstrap: "localStorage", // Cache flags in localStorage
			});

			// Wait for client to be ready
			await this.client.waitForInitialization();
			this.isInitialized = true;
			console.log("LaunchDarkly initialized successfully");
		} catch (error) {
			console.error("Failed to initialize LaunchDarkly:", error);
			this.isInitialized = false;
			throw error;
		}
	}

	/**
	 * Identify a new user
	 * @param user - User context
	 */
	public async identify(user: LDClient.LDUser): Promise<void> {
		if (!this.client) {
			console.warn("LaunchDarkly client not initialized");
			return;
		}

		try {
			await this.client.identify(user);
			console.log("User identified:", user.key);
		} catch (error) {
			console.error("Failed to identify user:", error);
		}
	}

	/**
	 * Get a feature flag value
	 * @param flagKey - The feature flag key
	 * @param defaultValue - Default value if flag not found
	 * @returns The flag value
	 */
	public variation<T = any>(flagKey: string, defaultValue: T): T {
		if (!this.client || !this.isInitialized) {
			console.warn(`LaunchDarkly client not initialized. Returning default value for flag: ${flagKey}`);
			return defaultValue;
		}

		try {
			return this.client.variation(flagKey, defaultValue) as T;
		} catch (error) {
			console.error(`Error getting flag ${flagKey}:`, error);
			return defaultValue;
		}
	}

	/**
	 * Get all feature flags
	 * @returns Object containing all flag values
	 */
	public allFlags(): LDClient.LDFlagSet {
		if (!this.client || !this.isInitialized) {
			console.warn("LaunchDarkly client not initialized");
			return {};
		}

		return this.client.allFlags();
	}

	/**
	 * Subscribe to flag changes
	 * @param flagKey - The feature flag key
	 * @param callback - Callback function when flag changes
	 */
	public on(flagKey: string, callback: (newValue: any, oldValue: any) => void): void {
		if (!this.client) {
			console.warn("LaunchDarkly client not initialized");
			return;
		}

		this.client.on(`change:${flagKey}`, callback);
	}

	/**
	 * Unsubscribe from flag changes
	 * @param flagKey - The feature flag key
	 * @param callback - The callback to remove
	 */
	public off(flagKey: string, callback: (newValue: any, oldValue: any) => void): void {
		if (!this.client) {
			return;
		}

		this.client.off(`change:${flagKey}`, callback);
	}

	/**
	 * Track a custom event
	 * @param eventName - Name of the custom event
	 * @param data - Optional data to send with the event
	 * @param metricValue - Optional numeric value for the event
	 */
	public track(eventName: string, data?: any, metricValue?: number): void {
		if (!this.client || !this.isInitialized) {
			console.warn("LaunchDarkly client not initialized");
			return;
		}

		try {
			this.client.track(eventName, data, metricValue);
		} catch (error) {
			console.error(`Error tracking event ${eventName}:`, error);
		}
	}

	/**
	 * Flush any pending events
	 */
	public async flush(): Promise<void> {
		if (!this.client) {
			return;
		}

		try {
			await this.client.flush();
		} catch (error) {
			console.error("Error flushing events:", error);
		}
	}

	/**
	 * Close the LaunchDarkly client
	 */
	public async close(): Promise<void> {
		if (!this.client) {
			return;
		}

		try {
			await this.client.close();
			this.client = null;
			this.isInitialized = false;
			this.initializationPromise = null;
			console.log("LaunchDarkly client closed");
		} catch (error) {
			console.error("Error closing LaunchDarkly client:", error);
		}
	}

	/**
	 * Check if client is initialized
	 */
	public getIsInitialized(): boolean {
		return this.isInitialized;
	}

	/**
	 * Resolves once the LD client has finished initializing — *or* finished
	 * failing. Safe to call from any component without knowing the client-side
	 * ID; `MainElement` owns the actual `initialize()` call.
	 *
	 * IMPORTANT: this method intentionally never rejects. `_initialize` re-throws
	 * on failure, so `initializationPromise` itself can reject. Returning that
	 * promise raw would force every caller to attach a `.catch()` purely to
	 * silence unhandled-rejection noise, and a missed `.catch()` would also
	 * skip the caller's "read flag values now that LD is settled" callback —
	 * leaving fallback defaults applied without ever re-reading. Use
	 * `.then(onResolve, onReject)` with both branches calling `resolve()` — not
	 * `.finally()` — because `finally` still forwards rejection to its *returned*
	 * promise, which would be an unhandled rejection if never consumed.
	 */
	public whenReady(): Promise<void> {
		if (this.isInitialized) return Promise.resolve();
		if (this.initializationPromise) {
			return new Promise((resolve) => {
				void this.initializationPromise!.then(
					() => resolve(),
					() => resolve()
				);
			});
		}

		return new Promise((resolve) => {
			const check = () => {
				if (this.isInitialized) return resolve();
				if (this.initializationPromise) {
					void this.initializationPromise.then(
						() => resolve(),
						() => resolve()
					);
					return;
				}
				setTimeout(check, 50);
			};
			setTimeout(check, 50);
		});
	}
}

// Export singleton instance
export const launchDarkly = LaunchDarklyService.getInstance();
